import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { ChatState } from '../states/chat-state';
import { ChatRoleEnum, ChatMessageEntity, ChatRequestEntity, AIModelEntity, AIAgentEntity, StreamingStatusEnum } from '@gishub/domain';
import { ChatRepositoryProvider, AIModelRepositoryProvider, AIAgentRepositoryProvider } from '../../shared/providers/repository-providers';
import { ChatShareRepositoryProvider } from '../../shared/providers/repository-providers';
import { UbigeoProvider } from '../providers/ubigeo-provider';
import { MapProvider } from '../providers/map-provider';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';

@Injectable({ providedIn: 'root' })
export class ChatNotifier extends BaseNotifier<ChatState> {
  private _sending = false;
  private _abortController: AbortController | null = null;

  constructor(ref: Ref) {
    super(ref, {
      messages: [],
      inputText: '',
      speakingIndex: null,
      isListening: false,
      streamingStatus: StreamingStatusEnum.Idle,
      streamingMessage: '',
      loadingOptions: true,
      selectedModel: null,
      selectedAgent: null,
      models: [],
      agents: [],
      chatLayers: [],
      regionScopeLabel: '',
      isStreaming: false,
      isBusy: false,
    });
  }

  private derive(s: ChatState): ChatState {
    return {
      ...s,
      isStreaming: s.streamingStatus !== StreamingStatusEnum.Idle,
      isBusy: s.loadingOptions || s.streamingStatus !== StreamingStatusEnum.Idle || s.isListening,
    };
  }

  override setState(newState: ChatState): void {
    super.setState(this.derive(newState));
  }

  override updateState(updater: (prev: ChatState) => ChatState): void {
    super.updateState((prev) => this.derive(updater(prev)));
  }

  override build(): void {
    this.loadModelsAndAgents();
    this.ref.createEffect(() => {
      const u = this.ref.read(UbigeoProvider).state();
      const parts: string[] = [];
      if (u.selectedDepartmentName) parts.push(u.selectedDepartmentName);
      if (u.selectedProvinceName) parts.push(` / ${u.selectedProvinceName}`);
      if (u.selectedDistrictName) parts.push(` / ${u.selectedDistrictName}`);
      this.updateState(prev => ({ ...prev, regionScopeLabel: parts.join('') || 'de Junín' }));
    });
  }

  private get repo() {
    return this.ref.read(ChatRepositoryProvider).instance;
  }

  private get modelRepo() {
    return this.ref.read(AIModelRepositoryProvider).instance;
  }

  private get agentRepo() {
    return this.ref.read(AIAgentRepositoryProvider).instance;
  }

  private async loadModelsAndAgents() {
    try {
      const [modelsRes, agentsRes] = await Promise.all([
        this.modelRepo.getAll(),
        this.agentRepo.getAll()
      ]);

      const rawModels = modelsRes.success ? modelsRes.data || [] : [];
      const models = Array.isArray(rawModels) ? rawModels : (rawModels as any).available || [];
      const agents = agentsRes.success ? agentsRes.data || [] : [];

      this.setState({
        ...this.getStateValue(),
        loadingOptions: false,
        models,
        agents,
        selectedModel: models.length > 0 ? models[0] : null,
        selectedAgent: agents.length > 0 ? agents[0] : null,
      });
    } catch (error) {
      console.error('Error loading models and agents:', error);
      this.setState({
        ...this.getStateValue(),
        loadingOptions: false,
      });
    }
  }

  setSelectedModel(model: AIModelEntity | null) {
    this.setState({ ...this.getStateValue(), selectedModel: model });
  }

  setSelectedAgent(agent: AIAgentEntity | null) {
    this.setState({ ...this.getStateValue(), selectedAgent: agent });
  }

  setInputText(text: string) {
    this.setState({ ...this.getStateValue(), inputText: text });
  }

  /**
   * Helper: actualiza el mensaje AI en el índice dado con los campos proporcionados.
   * Elimina la repetición de 4 bloques idénticos en el stream loop.
   */
  private updateAiMessage(
    index: number,
    patch: Partial<Pick<ChatMessageEntity, 'blocks' | 'isError'>>
  ): void {
    const curr = this.getStateValue();
    const updated = [...curr.messages];
    updated[index] = { ...updated[index], ...patch };
    this.setState({ ...curr, messages: updated });
  }

  /**
   * Helper: actualiza solo el campo `streamingStatus` y `streamingMessage`.
   */
  private setStreaming(status: StreamingStatusEnum, message = '') {
    this.setState({ ...this.getStateValue(), streamingStatus: status, streamingMessage: message });
  }

  async sendMessage(text: string): Promise<void> {
    text = text.trim();
    if (!text || this._sending) return;

    const state = this.getStateValue();
    if (!state.selectedModel || !state.selectedAgent) {
      console.error('Model or agent not selected');
      return;
    }

    this.setStreaming(StreamingStatusEnum.Connecting, 'Conectando...');
    const msgs: ChatMessageEntity[] = [...state.messages, { role: ChatRoleEnum.User, blocks: [{ kind: 'text' as const, content: text }] }];
    const aiIndex = msgs.length;
    msgs.push({ role: ChatRoleEnum.Ai, blocks: [] });
    this.setState({ ...state, messages: msgs, inputText: '' });

    await this.startStream(text, state, aiIndex);
  }

  async retryMessage(text: string, aiIndex: number): Promise<void> {
    text = text.trim();
    if (!text || this._sending) return;

    const state = this.getStateValue();
    if (!state.selectedModel || !state.selectedAgent) return;

    const msgs = [...state.messages];
    msgs[aiIndex] = { role: ChatRoleEnum.Ai, blocks: [] };
    this.setState({ ...state, messages: msgs });

    await this.startStream(text, state, aiIndex);
  }

  private async startStream(text: string, state: ChatState, aiIndex: number): Promise<void> {
    this._sending = true;
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    const request: ChatRequestEntity = {
      input: text,
      agentId: state.selectedAgent!.id,
      sessionId: 'session-' + Date.now(),
      modelId: state.selectedModel!.id
    };

    try {
      const streamRes = await this.repo.sendMessageStream(request, signal);

      if (!streamRes.success || !streamRes.data) {
        this.setStreaming(StreamingStatusEnum.Error, 'No se pudo conectar con el servidor.');
        this.updateAiMessage(aiIndex, { blocks: [{ kind: 'text', content: 'Lo siento, no pude procesar tu consulta. Intenta de nuevo.' }] });
        return;
      }

      const response = streamRes.data;
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        this.setStreaming(StreamingStatusEnum.Error, 'No se pudo obtener el stream.');
        this.updateAiMessage(aiIndex, { blocks: [{ kind: 'text', content: 'Error: no se pudo establecer la conexión con el stream.' }] });
        return;
      }

      const blocks: any[] = [];
      this.setStreaming(StreamingStatusEnum.Streaming, 'Generando respuesta...');

      let buffer = '';

      while (true) {
        if (signal.aborted) break;

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.substring(6);
          try {
            const data = JSON.parse(jsonStr);

            switch (data.type) {
              case 'text':
                {
                  const last = blocks[blocks.length - 1];
                  if (last?.kind === 'text') {
                    last.content += data.text;
                  } else {
                    blocks.push({ kind: 'text', content: data.text });
                  }
                }
                this.updateAiMessage(aiIndex, { blocks: [...blocks] });
                break;

              case 'chart':
                if (data.chartConfig) {
                  blocks.push({ kind: 'chart', config: data.chartConfig });
                  this.updateAiMessage(aiIndex, { blocks: [...blocks] });
                }
                break;

              case 'geojson':
                if (data.geojson) {
                  blocks.push({ kind: 'geojson', data: data.geojson });
                  this.updateAiMessage(aiIndex, { blocks: [...blocks] });
                }
                break;

              case 'esrijson':
                if (data.esrijson) {
                  blocks.push({ kind: 'esrijson', data: data.esrijson });
                  this.updateAiMessage(aiIndex, { blocks: [...blocks] });
                }
                break;

              case 'progress':
              case 'tool_start':
              case 'tool_done':
                this.setStreaming(StreamingStatusEnum.Streaming, data.progressMessage || 'Procesando...');
                break;

              case 'done':
                this.setStreaming(StreamingStatusEnum.Idle);
                break;

              case 'fail':
                {
                  const last = blocks[blocks.length - 1];
                  const errorText = `❌ ${data.error || 'Error durante la generación.'}`;
                  if (last?.kind === 'text') {
                    last.content += '\n\n' + errorText;
                  } else {
                    blocks.push({ kind: 'text', content: errorText });
                  }
                }
                this.updateAiMessage(aiIndex, { blocks: [...blocks], isError: true });
                this.setStreaming(StreamingStatusEnum.Error, data.error || 'Error durante la generación.');
                break;

              case 'error':
                console.error('Stream error:', data.error);
                this.setStreaming(StreamingStatusEnum.Error, data.error || 'Error durante la generación.');
                break;

            }
          } catch (e) {
            console.error('Error parsing SSE data:', jsonStr, e);
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('[ChatNotifier] Stream cancelled by user');
      } else {
        console.error('Error reading stream:', error);
        this.setStreaming(StreamingStatusEnum.Error, 'Error de conexión durante la transmisión.');
      }
    } finally {
      this._sending = false;
      this._abortController = null;
      this.setStreaming(StreamingStatusEnum.Idle);
    }
  }

  private textFromMsg(msg: ChatMessageEntity): string {
    return msg.blocks?.map(b => b.kind === 'text' ? b.content : '').join('') ?? '';
  }

  private speakText(text: string): Promise<void> {
    return new Promise(resolve => {
      const u = new SpeechSynthesisUtterance(text.replace(/\*\*(.*?)\*\*/g, '$1'));
      u.lang = 'es-PE';
      u.rate = 1.1;
      u.pitch = 1;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      speechSynthesis.speak(u);
    });
  }

  shareMessage(index: number): string {
    const msgs = this.getStateValue().messages;
    const aiMsg = msgs[index];
    const userMsg = msgs.slice(0, index).reverse().find(m => m.role === ChatRoleEnum.User);
    if (!userMsg) return '';
    const id = this.ref.read(ChatShareRepositoryProvider).instance.save({ userMsg, aiMsg, createdAt: Date.now() });
    const url = `${window.location.origin}/share/${id}`;
    navigator.clipboard.writeText(url);
    return url;
  }

  cancelStream(): void {
    this._abortController?.abort();
  }

  copyMsg(index: number): void {
    const msg = this.getStateValue().messages[index];
    if (!msg) return;
    const text = msg.blocks
      ?.filter((b: any) => b.kind === 'text')
      .map((b: any) => b.content)
      .join(' ') ?? '';
    navigator.clipboard.writeText(text);
  }

  addGeojsonToMap(geojson: any): void {
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map || !mapState.view) return;
    try {
      const blob = new Blob([JSON.stringify(geojson)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const layer = new GeoJSONLayer({
        url,
        title: 'Chat - GeoJSON',
        id: `chat-geojson-${Date.now()}`,
        popupTemplate: { title: '{*}', content: '{*}' },
      });
      mapState.map.add(layer);
      layer.when(
        () => mapState.view!.goTo(layer.fullExtent!),
        (err: any) => console.error('[ChatNotifier] GeoJSONLayer error:', err),
      );
    } catch (error) {
      console.error('[ChatNotifier] Error adding GeoJSON layer:', error);
    }
  }

  addEsrijsonToMap(esriJson: any): void {
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map || !mapState.view) return;
    try {
      const graphics = esriJson.features
        .map((f: any) => Graphic.fromJSON(f))
        .filter((g: any) => g?.geometry);
      const layer = new GraphicsLayer({
        title: 'Chat - EsriJSON',
        id: `chat-esrijson-${Date.now()}`,
      });
      layer.addMany(graphics);
      mapState.map.add(layer);
      if (graphics.length > 0) {
        mapState.view.goTo(graphics);
      }
    } catch (error) {
      console.error('[ChatNotifier] Error adding EsriJSON layer:', error);
    }
  }

  async toggleAudio(index: number): Promise<void> {
    const state = this.getStateValue();
    const was = state.speakingIndex;

    if (was === index) {
      speechSynthesis.cancel();
      this.setState({ ...this.getStateValue(), speakingIndex: null });
      return;
    }

    speechSynthesis.cancel();
    this.setState({ ...this.getStateValue(), speakingIndex: index });

    const msg = state.messages[index];
    if (msg && msg.role === ChatRoleEnum.Ai) {
      await this.speakText(this.textFromMsg(msg));
    }

    const curr = this.getStateValue();
    if (curr.speakingIndex === index) {
      this.setState({ ...curr, speakingIndex: null });
    }
  }

  private recognition: any = null;

  startListening(): void {
    if (this.getStateValue().isListening) return;
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;

    this.recognition = new SR();
    this.recognition.lang = 'es-PE';
    this.recognition.continuous = false;
    this.recognition.interimResults = false;

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      const text = event.results[0][0].transcript;
      this.setState({ ...this.getStateValue(), inputText: text });
    };

    this.recognition.onend = () => {
      this.recognition = null;
      this.setState({ ...this.getStateValue(), isListening: false });
    };

    this.recognition.onerror = () => {
      this.recognition = null;
      this.setState({ ...this.getStateValue(), isListening: false });
    };

    this.setState({ ...this.getStateValue(), isListening: true });
    this.recognition.start();
  }

  stopListening(): void {
    if (this.recognition) {
      this.recognition.abort();
      this.recognition = null;
    }
    this.setState({ ...this.getStateValue(), isListening: false });
  }
}
